import type { ComponentProps } from "react";
import { Tabs as TabsPrimitive } from "@base-ui/react/tabs";
import classNames from "classnames";
import styles from "./Tabs.module.scss";

// Root
interface TabsRootProps extends ComponentProps<typeof TabsPrimitive.Root> {
    className?: string;
}
// eslint-disable-next-line react-refresh/only-export-components
const TabsRoot = ({ className, children, ...props }: TabsRootProps) => (
    <TabsPrimitive.Root
        className={classNames(styles.root, className)}
        {...props}
    >
        {children}
    </TabsPrimitive.Root>
);

// List
interface TabsListProps extends ComponentProps<typeof TabsPrimitive.List> {
    className?: string;
    /** Leave out the sliding active tab indicator when the tabs draw their own. */
    hasIndicator?: boolean;
    /** Restyles the active tab indicator, which keeps its slide. */
    indicatorClassName?: string;
}
// eslint-disable-next-line react-refresh/only-export-components
const TabsList = ({
    className,
    hasIndicator = true,
    indicatorClassName,
    children,
    ...props
}: TabsListProps) => (
    <TabsPrimitive.List
        className={classNames(styles.list, className)}
        {...props}
    >
        {children}
        {hasIndicator && (
            <TabsPrimitive.Indicator
                className={classNames(styles.indicator, indicatorClassName)}
            />
        )}
    </TabsPrimitive.List>
);

// Tab
interface TabsTabProps extends ComponentProps<typeof TabsPrimitive.Tab> {
    className?: string;
}
// eslint-disable-next-line react-refresh/only-export-components
const TabsTab = ({ className, children, ...props }: TabsTabProps) => (
    <TabsPrimitive.Tab className={classNames(styles.tab, className)} {...props}>
        {children}
    </TabsPrimitive.Tab>
);

// Panel
interface TabsPanelProps extends ComponentProps<typeof TabsPrimitive.Panel> {
    className?: string;
}
// eslint-disable-next-line react-refresh/only-export-components
const TabsPanel = ({ className, children, ...props }: TabsPanelProps) => (
    <TabsPrimitive.Panel
        className={classNames(styles.panel, className)}
        {...props}
    >
        {children}
    </TabsPrimitive.Panel>
);

export const Tabs = {
    Root: TabsRoot,
    List: TabsList,
    Tab: TabsTab,
    Panel: TabsPanel,
};
